/**
 * tRPC-merging hook for the order-detail screen: fetches one order plus the
 * client's product list (the order carries productIds, not names) and shapes
 * both into a ready-to-render view via orderHistoryViewModel's pure helpers.
 *
 * Mirrors useOrderHistory — the page below it only renders. The NOT_FOUND case
 * is surfaced as its own flag rather than an error code the page has to decode,
 * because it is a state with its own copy, not a failure to report.
 */
import {useMemo} from 'react'
import type {CutoffRule} from '@retail/dates'
import {trpc} from '../../trpc'
import {getErrorCode} from '../../lib/errorCode'
import {useNowAcrossCutoff} from '../../hooks/useNowAcrossCutoff'
import {toOrderDetailView} from './orderHistoryViewModel'
import type {OrderDetailView} from './orderHistoryViewModel'

type UseOrderDetailInput = {
  storeId: number
  /** The `:orderId` path param, already parsed — NaN when the URL is malformed. */
  orderId: number
  /** The store's own cutoff rule; absent = the general one. Decides which week the "Editar pedido" CTA may open. */
  cutoff?: CutoffRule
}

export type OrderDetailViewModel = {
  /** Shaped order, or undefined while loading / on error. */
  detail: OrderDetailView | undefined
  isError: boolean
  error: unknown
  /** The order does not exist (or is not this store's) — its own empty state. */
  isNotFound: boolean
}

export const useOrderDetail = ({storeId, orderId, cutoff}: UseOrderDetailInput): OrderDetailViewModel => {
  const isValidOrderId = Number.isFinite(orderId)

  // Product-name resolution: build a productId→name map from the client's full
  // product list. No dedicated endpoint — catalog.products.list already exists.
  // Gated like orderQuery: with a malformed id the screen short-circuits to
  // not-found and the names would go unused. Unlike the categories query in
  // useWeeklyProposal (left unconditional to avoid a waterfall), the gate is
  // known synchronously from the URL, so on a valid link both queries still
  // start in parallel.
  const productsQuery = trpc.catalog.products.list.useQuery({}, {enabled: isValidOrderId})
  const productNameById = useMemo(() => {
    const byId = new Map<number, string>()
    for (const product of productsQuery.data ?? []) byId.set(product.id, product.name)
    return byId
  }, [productsQuery.data])

  const orderQuery = trpc.orders.byId.useQuery(
    {storeId, orderId},
    {enabled: isValidOrderId},
  )

  const order = orderQuery.data
  // The status and the "Editar pedido" CTA are derived from the clock, so the
  // instant has to be a value that moves when the cutoff passes — not a
  // `new Date()` captured in the memo below, which would freeze the order as
  // editable while the countdown next to it already read "Cerrado".
  const nowMs = useNowAcrossCutoff(order?.cutoffAt)

  const detail = useMemo(() => {
    if (order === undefined) return undefined
    const resolveProductName = (productId: number): string =>
      productNameById.get(productId) ?? `#${productId}`
    return toOrderDetailView(order, resolveProductName, new Date(nowMs), cutoff)
  }, [order, productNameById, nowMs, cutoff])

  return {
    detail,
    isError: orderQuery.isError,
    error: orderQuery.error,
    // A malformed id in the URL is the same dead end as an unknown order.
    isNotFound:
      !isValidOrderId || (orderQuery.isError && getErrorCode(orderQuery.error) === 'NOT_FOUND'),
  }
}
